import React, { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plug, Key, PaperPlaneTilt, ArrowClockwise, Trash, Copy, Plus,
  CheckCircle, WarningCircle, TerminalWindow, PencilSimple,
} from "@phosphor-icons/react";

/* ────────────────────────────────────────────────────────────────────── */
/*                            HMAC helpers                                */
/* ────────────────────────────────────────────────────────────────────── */
async function sha256Hex(str) {
  const buf = new TextEncoder().encode(str);
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

async function hmacSha256Hex(secret, message) {
  const key = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const sig = await crypto.subtle.sign("HMAC", key, new TextEncoder().encode(message));
  return Array.from(new Uint8Array(sig))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function copy(text, label = "Tersalin") {
  navigator.clipboard.writeText(text).then(
    () => toast.success(label),
    () => toast.error("Gagal menyalin"),
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("id-ID", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

/* ────────────────────────────────────────────────────────────────────── */
/*                             API KEYS TAB                               */
/* ────────────────────────────────────────────────────────────────────── */
function ApiKeysTab() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newLimit, setNewLimit] = useState(60);
  const [created, setCreated] = useState(null); // {api_key, api_secret, name}
  const [editing, setEditing] = useState(null); // {id, rate_limit_per_min}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/api-keys");
      setKeys(data);
    } catch (err) {
      toast.error(formatApiError(err, "Gagal memuat API keys"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onCreate = async () => {
    if (!newName.trim()) return toast.error("Nama wajib diisi");
    try {
      const { data } = await api.post("/settings/api-keys", {
        name: newName.trim(),
        rate_limit_per_min: Number(newLimit) || 60,
      });
      setCreated(data);
      setShowNew(false);
      setNewName(""); setNewLimit(60);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Gagal membuat API key"));
    }
  };

  const onRevoke = async (id, name) => {
    if (!window.confirm(`Revoke API key "${name}"? Sistem yang menggunakan key ini akan langsung terputus.`)) return;
    try {
      await api.delete(`/settings/api-keys/${id}`);
      toast.success("API key direvoke");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Gagal revoke"));
    }
  };

  const onSaveLimit = async () => {
    if (!editing) return;
    const rlim = Number(editing.rate_limit_per_min);
    if (!Number.isFinite(rlim) || rlim < 1 || rlim > 10000) {
      return toast.error("Rate limit harus 1 – 10000 request/menit");
    }
    try {
      await api.patch(`/settings/api-keys/${editing.id}`, { rate_limit_per_min: rlim });
      toast.success("Rate limit diperbarui");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Gagal simpan rate limit"));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Key size={20} weight="duotone" /> API Keys
          </h3>
          <p className="text-sm text-muted-foreground">
            Kunci untuk otentikasi HMAC-SHA256 pada endpoint <code>/api/v1/*</code>.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="api-keys-refresh-btn">
            <ArrowClockwise size={16} className="mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={() => setShowNew(true)} data-testid="api-keys-new-btn">
            <Plus size={16} className="mr-1" /> Buat Baru
          </Button>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nama</TableHead>
              <TableHead>Prefix</TableHead>
              <TableHead>Rate Limit</TableHead>
              <TableHead>Dibuat</TableHead>
              <TableHead>Terakhir Dipakai</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[120px]">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">Memuat…</TableCell></TableRow>
            )}
            {!loading && keys.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">Belum ada API key. Klik "Buat Baru" untuk mulai.</TableCell></TableRow>
            )}
            {keys.map((k) => (
              <TableRow key={k.id} data-testid={`api-key-row-${k.id}`}>
                <TableCell className="font-medium">{k.name}</TableCell>
                <TableCell><code className="text-xs">{k.key_prefix}</code></TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="tabular-nums">{k.rate_limit_per_min}/mnt</span>
                    {!k.revoked_at && (
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0"
                        onClick={() => setEditing({ id: k.id, rate_limit_per_min: k.rate_limit_per_min })}
                        data-testid={`api-key-edit-limit-${k.id}`}>
                        <PencilSimple size={14} />
                      </Button>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-sm">{fmtDate(k.created_at)}</TableCell>
                <TableCell className="text-sm">{fmtDate(k.last_used_at)}</TableCell>
                <TableCell>
                  {k.revoked_at
                    ? <Badge variant="destructive">Revoked</Badge>
                    : <Badge variant="default">Aktif</Badge>}
                </TableCell>
                <TableCell>
                  {!k.revoked_at && (
                    <Button variant="ghost" size="sm" onClick={() => onRevoke(k.id, k.name)}
                      data-testid={`api-key-revoke-${k.id}`}>
                      <Trash size={16} className="text-destructive" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent data-testid="api-key-create-dialog">
          <DialogHeader>
            <DialogTitle>Buat API Key baru</DialogTitle>
            <DialogDescription>Nama membantu identifikasi. Rate limit membatasi request per menit per key.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="new-key-name">Nama</Label>
              <Input id="new-key-name" value={newName} onChange={(e) => setNewName(e.target.value)}
                placeholder="mis. ERP-Produksi, Mobile-Sopir" data-testid="new-key-name-input" />
            </div>
            <div>
              <Label htmlFor="new-key-limit">Rate limit (req/menit)</Label>
              <Input id="new-key-limit" type="number" min="1" max="10000" value={newLimit}
                onChange={(e) => setNewLimit(e.target.value)} data-testid="new-key-limit-input" />
              <p className="text-xs text-muted-foreground mt-1">Default 60. Bisa diubah nanti.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Batal</Button>
            <Button onClick={onCreate} data-testid="new-key-submit-btn">Buat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit limit dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent data-testid="api-key-edit-dialog">
          <DialogHeader>
            <DialogTitle>Ubah Rate Limit</DialogTitle>
          </DialogHeader>
          <div>
            <Label htmlFor="edit-limit">Request per menit</Label>
            <Input id="edit-limit" type="number" min="1" max="10000"
              value={editing?.rate_limit_per_min ?? ""}
              onChange={(e) => setEditing({ ...editing, rate_limit_per_min: e.target.value })}
              data-testid="edit-limit-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Batal</Button>
            <Button onClick={onSaveLimit} data-testid="edit-limit-save-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Show-once secret dialog */}
      <Dialog open={!!created} onOpenChange={(open) => !open && setCreated(null)}>
        <DialogContent className="max-w-2xl" data-testid="api-key-created-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <WarningCircle size={22} weight="fill" />
              Simpan sekarang — nilai tidak akan muncul lagi
            </DialogTitle>
            <DialogDescription>
              Salin <code>api_key</code> dan <code>api_secret</code> ke tempat aman. Jika hilang, buat key baru dan revoke yang lama.
            </DialogDescription>
          </DialogHeader>
          {created && (
            <div className="space-y-3">
              <div>
                <Label>Nama</Label>
                <div className="font-medium">{created.name}</div>
              </div>
              <div>
                <Label>API Key</Label>
                <div className="flex gap-2">
                  <Input readOnly value={created.api_key} className="font-mono text-xs" />
                  <Button size="sm" variant="outline" onClick={() => copy(created.api_key, "API Key disalin")}
                    data-testid="copy-api-key-btn"><Copy size={16} /></Button>
                </div>
              </div>
              <div>
                <Label>API Secret</Label>
                <div className="flex gap-2">
                  <Input readOnly value={created.api_secret} className="font-mono text-xs" />
                  <Button size="sm" variant="outline" onClick={() => copy(created.api_secret, "Secret disalin")}
                    data-testid="copy-api-secret-btn"><Copy size={16} /></Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setCreated(null)} data-testid="close-created-dialog-btn">Saya sudah menyimpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/*                             WEBHOOKS TAB                               */
/* ────────────────────────────────────────────────────────────────────── */
function WebhooksTab() {
  const [hooks, setHooks] = useState([]);
  const [events, setEvents] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", url: "", events: [], active: true });
  const [created, setCreated] = useState(null); // {secret, ...}
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [hRes, eRes, qRes] = await Promise.all([
        api.get("/settings/webhooks"),
        api.get("/settings/webhooks/events"),
        api.get("/settings/webhooks/queue?limit=50"),
      ]);
      setHooks(hRes.data);
      setEvents(eRes.data);
      setQueue(qRes.data);
    } catch (err) {
      toast.error(formatApiError(err, "Gagal memuat webhooks"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm({ name: "", url: "", events: [], active: true });
    setShowNew(true);
  };

  const openEdit = (h) => {
    setEditing({ id: h.id, name: h.name, url: h.url, events: [...(h.events || [])], active: !!h.active });
  };

  const toggleEvent = (state, setState, ev) => {
    const has = state.events.includes(ev);
    setState({ ...state, events: has ? state.events.filter((e) => e !== ev) : [...state.events, ev] });
  };

  const onCreate = async () => {
    if (!form.name.trim() || !form.url.trim()) return toast.error("Nama & URL wajib.");
    if (form.events.length === 0) return toast.error("Pilih minimal satu event.");
    try {
      const { data } = await api.post("/settings/webhooks", {
        name: form.name.trim(),
        url: form.url.trim(),
        events: form.events,
        active: form.active,
      });
      setCreated(data);
      setShowNew(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Gagal buat webhook"));
    }
  };

  const onUpdate = async () => {
    if (!editing) return;
    if (!editing.name.trim() || !editing.url.trim()) return toast.error("Nama & URL wajib.");
    if (editing.events.length === 0) return toast.error("Pilih minimal satu event.");
    try {
      await api.patch(`/settings/webhooks/${editing.id}`, {
        name: editing.name.trim(),
        url: editing.url.trim(),
        events: editing.events,
        active: editing.active,
      });
      toast.success("Webhook diperbarui");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Gagal update webhook"));
    }
  };

  const onDelete = async (h) => {
    if (!window.confirm(`Hapus webhook "${h.name}"?`)) return;
    try {
      await api.delete(`/settings/webhooks/${h.id}`);
      toast.success("Webhook dihapus");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Gagal hapus"));
    }
  };

  const onTest = async (h) => {
    try {
      const { data } = await api.post(`/settings/webhooks/${h.id}/test`);
      toast.success(`Test terkirim. ${data?.counters?.delivered ?? 0} delivered, ${data?.counters?.failed ?? 0} gagal.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Gagal test webhook"));
    }
  };

  const statusBadge = (s) => {
    if (s === "delivered") return <Badge className="bg-emerald-600">Delivered</Badge>;
    if (s === "pending") return <Badge variant="secondary">Pending</Badge>;
    if (s === "failed") return <Badge variant="destructive">Failed</Badge>;
    return <Badge variant="outline">{s}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <PaperPlaneTilt size={20} weight="duotone" /> Webhook Subscriptions
          </h3>
          <p className="text-sm text-muted-foreground">
            Terima notifikasi event SPB (created, status_changed, dsb.) di endpoint eksternal. Payload ditandatangani HMAC-SHA256.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="webhooks-refresh-btn">
            <ArrowClockwise size={16} className="mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={openNew} data-testid="webhooks-new-btn">
            <Plus size={16} className="mr-1" /> Tambah
          </Button>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nama</TableHead>
              <TableHead>URL</TableHead>
              <TableHead>Events</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Terakhir Kirim</TableHead>
              <TableHead className="w-[160px]">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">Memuat…</TableCell></TableRow>
            )}
            {!loading && hooks.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">Belum ada webhook.</TableCell></TableRow>
            )}
            {hooks.map((h) => (
              <TableRow key={h.id} data-testid={`webhook-row-${h.id}`}>
                <TableCell className="font-medium">{h.name}</TableCell>
                <TableCell className="max-w-[260px] truncate font-mono text-xs" title={h.url}>{h.url}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {h.events.map((e) => <Badge key={e} variant="outline" className="text-xs">{e}</Badge>)}
                  </div>
                </TableCell>
                <TableCell>
                  {h.active
                    ? <Badge className="bg-emerald-600">Aktif</Badge>
                    : <Badge variant="secondary">Nonaktif</Badge>}
                  {h.last_status_code && (
                    <div className="text-xs text-muted-foreground mt-1">HTTP {h.last_status_code}</div>
                  )}
                </TableCell>
                <TableCell className="text-sm">{fmtDate(h.last_delivery_at)}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => onTest(h)} title="Kirim payload test"
                      data-testid={`webhook-test-${h.id}`}>
                      <PaperPlaneTilt size={16} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(h)} title="Edit"
                      data-testid={`webhook-edit-${h.id}`}>
                      <PencilSimple size={16} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => onDelete(h)} title="Hapus"
                      data-testid={`webhook-delete-${h.id}`}>
                      <Trash size={16} className="text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Delivery queue */}
      <div>
        <h4 className="text-sm font-semibold uppercase text-muted-foreground mb-2">Riwayat Pengiriman (50 terakhir)</h4>
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Waktu</TableHead>
                <TableHead>Webhook</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Percobaan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Error</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {queue.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-4 text-muted-foreground">Belum ada aktivitas.</TableCell></TableRow>
              )}
              {queue.map((q) => (
                <TableRow key={q.id}>
                  <TableCell className="text-xs">{fmtDate(q.updated_at || q.created_at)}</TableCell>
                  <TableCell className="text-sm">{q.webhook_name}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{q.event}</Badge></TableCell>
                  <TableCell className="text-sm">{q.attempts}/{q.max_attempts}</TableCell>
                  <TableCell>{statusBadge(q.status)}{q.last_status_code ? <span className="text-xs text-muted-foreground ml-2">HTTP {q.last_status_code}</span> : null}</TableCell>
                  <TableCell className="text-xs max-w-[280px] truncate" title={q.last_error}>{q.last_error || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-lg" data-testid="webhook-create-dialog">
          <DialogHeader>
            <DialogTitle>Tambah Webhook</DialogTitle>
            <DialogDescription>Setelah dibuat, secret HMAC ditampilkan sekali untuk verifikasi payload di sisi penerima.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nama</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="mis. ERP-Notifikasi-Prod" data-testid="webhook-name-input" />
            </div>
            <div>
              <Label>URL Endpoint (HTTPS disarankan)</Label>
              <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://erp.saribakti.co.id/webhooks/epool" data-testid="webhook-url-input" />
            </div>
            <div>
              <Label>Events</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {events.map((ev) => (
                  <label key={ev} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.events.includes(ev)}
                      onChange={() => toggleEvent(form, setForm, ev)}
                      data-testid={`webhook-event-${ev}`} />
                    <code className="text-xs">{ev}</code>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <Label>Aktif</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Batal</Button>
            <Button onClick={onCreate} data-testid="webhook-create-submit-btn">Buat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="webhook-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Webhook</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Nama</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div>
                <Label>URL</Label>
                <Input value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} />
              </div>
              <div>
                <Label>Events</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {events.map((ev) => (
                    <label key={ev} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={editing.events.includes(ev)}
                        onChange={() => toggleEvent(editing, setEditing, ev)} />
                      <code className="text-xs">{ev}</code>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                <Label>Aktif</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Batal</Button>
            <Button onClick={onUpdate} data-testid="webhook-edit-save-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Show-once secret */}
      <Dialog open={!!created} onOpenChange={(open) => !open && setCreated(null)}>
        <DialogContent className="max-w-2xl" data-testid="webhook-created-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <WarningCircle size={22} weight="fill" /> Simpan secret sekarang
            </DialogTitle>
            <DialogDescription>
              Gunakan secret ini untuk verifikasi signature di sisi penerima.
              Payload dikirim dengan header <code>X-Epool-Signature</code>: <code>hex(HMAC-SHA256(secret, timestamp + "." + body))</code>.
            </DialogDescription>
          </DialogHeader>
          {created && (
            <div className="space-y-3">
              <div>
                <Label>Webhook</Label>
                <div className="font-medium">{created.name} → <span className="font-mono text-xs">{created.url}</span></div>
              </div>
              <div>
                <Label>Secret</Label>
                <div className="flex gap-2">
                  <Input readOnly value={created.secret || ""} className="font-mono text-xs" />
                  <Button size="sm" variant="outline" onClick={() => copy(created.secret, "Secret disalin")}
                    data-testid="copy-webhook-secret-btn"><Copy size={16} /></Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setCreated(null)}>Saya sudah menyimpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/*                            PLAYGROUND TAB                              */
/* ────────────────────────────────────────────────────────────────────── */
const ENDPOINT_PRESETS = [
  { label: "GET /api/v1/health", method: "GET", path: "/api/v1/health", body: "" },
  { label: "GET /api/v1/lookup/plants", method: "GET", path: "/api/v1/lookup/plants", body: "" },
  { label: "GET /api/v1/lookup/products", method: "GET", path: "/api/v1/lookup/products", body: "" },
  { label: "GET /api/v1/lookup/districts", method: "GET", path: "/api/v1/lookup/districts", body: "" },
  { label: "GET /api/v1/lookup/drivers", method: "GET", path: "/api/v1/lookup/drivers", body: "" },
  { label: "GET /api/v1/lookup/vehicles", method: "GET", path: "/api/v1/lookup/vehicles", body: "" },
  { label: "GET /api/v1/lookup/sales-orders", method: "GET", path: "/api/v1/lookup/sales-orders", body: "" },
  { label: "GET /api/v1/spb/{no_spb}", method: "GET", path: "/api/v1/spb/SPB-2026-0001", body: "" },
  {
    label: "POST /api/v1/spb", method: "POST", path: "/api/v1/spb",
    body: JSON.stringify({
      tanggal: new Date().toISOString().slice(0, 10),
      plant_id: "<PLANT_ID>",
      produk_id: "<PRODUK_ID>",
      distrik_id: "<DISTRIK_ID>",
      no_polisi: "BK1234AB",
      nama_supir: "Nama Supir",
      jumlah: 100,
      satuan: "TON",
      nama_barang: "Semen Padang",
      no_so: "SO-2026-0001",
      so_allocations: [{ so_id: "<SO_ID>", qty: 100 }],
      nama_pelanggan: "PT Contoh Distributor",
      alamat_pengiriman: "Jl. Contoh No. 1, Medan",
    }, null, 2),
  },
];

function PlaygroundTab() {
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [method, setMethod] = useState("GET");
  const [path, setPath] = useState("/api/v1/health");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {status, headers, body, curl, duration_ms}

  const applyPreset = (label) => {
    const p = ENDPOINT_PRESETS.find((x) => x.label === label);
    if (!p) return;
    setMethod(p.method); setPath(p.path); setBody(p.body);
  };

  const backendOrigin = useMemo(() => {
    const u = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return u.replace(/\/$/, "");
  }, []);

  const buildCurl = (ts, sig, bodyStr) => {
    const url = `${backendOrigin}${path}`;
    const parts = [
      `curl -X ${method} '${url}'`,
      `  -H 'X-API-Key: ${apiKey || "<API_KEY>"}'`,
      `  -H 'X-API-Timestamp: ${ts}'`,
      `  -H 'X-API-Signature: ${sig}'`,
    ];
    if (method !== "GET" && method !== "DELETE") {
      parts.push(`  -H 'Content-Type: application/json'`);
      parts.push(`  --data '${(bodyStr || "").replace(/'/g, "'\\''")}'`);
    }
    return parts.join(" \\\n");
  };

  const onSend = async () => {
    if (!apiKey || !apiSecret) return toast.error("Isi API Key & Secret dulu.");
    if (!path.startsWith("/api/v1/")) return toast.error("Path harus diawali /api/v1/");
    const bodyStr = method === "GET" || method === "DELETE" ? "" : (body || "");
    // Validate JSON if provided
    if (bodyStr) {
      try { JSON.parse(bodyStr); } catch { return toast.error("Body bukan JSON valid."); }
    }
    const ts = Math.floor(Date.now() / 1000).toString();
    const bodyHash = await sha256Hex(bodyStr);
    const canonical = `${ts}\n${method}\n${path}\n${bodyHash}`;
    const sig = await hmacSha256Hex(apiSecret, canonical);
    const curlCmd = buildCurl(ts, sig, bodyStr);

    setBusy(true);
    const t0 = performance.now();
    try {
      const url = `${backendOrigin}${path}`;
      const fetchOpts = {
        method,
        headers: {
          "X-API-Key": apiKey,
          "X-API-Timestamp": ts,
          "X-API-Signature": sig,
        },
      };
      if (bodyStr) {
        fetchOpts.headers["Content-Type"] = "application/json";
        fetchOpts.body = bodyStr;
      }
      const res = await fetch(url, fetchOpts);
      const dt = Math.round(performance.now() - t0);
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = text; }
      const headers = {};
      res.headers.forEach((v, k) => { headers[k] = v; });
      setResult({
        status: res.status,
        ok: res.ok,
        headers,
        body: parsed,
        curl: curlCmd,
        duration_ms: dt,
      });
      if (res.ok) toast.success(`${res.status} ${res.statusText} (${dt}ms)`);
      else toast.error(`${res.status} ${res.statusText}`);
    } catch (err) {
      setResult({ status: 0, ok: false, body: String(err), curl: curlCmd, duration_ms: 0, headers: {} });
      toast.error("Request gagal: " + String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <TerminalWindow size={20} weight="duotone" /> API Playground
        </h3>
        <p className="text-sm text-muted-foreground">
          Test call endpoint <code>/api/v1/*</code> langsung dari browser. Signature HMAC dihitung otomatis di sisi klien.
        </p>
      </div>

      <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>API Key</Label>
            <Input value={apiKey} onChange={(e) => setApiKey(e.target.value)}
              placeholder="epk_..." className="font-mono text-xs" data-testid="playground-api-key" />
          </div>
          <div>
            <Label>API Secret</Label>
            <Input type="password" value={apiSecret} onChange={(e) => setApiSecret(e.target.value)}
              placeholder="eps_..." className="font-mono text-xs" data-testid="playground-api-secret" />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Nilai ini tidak disimpan di server — hanya di memori browser sampai halaman ditutup.
        </p>
      </div>

      <div className="border rounded-lg p-4 space-y-3">
        <div>
          <Label>Preset</Label>
          <Select onValueChange={applyPreset}>
            <SelectTrigger data-testid="playground-preset-select"><SelectValue placeholder="Pilih endpoint contoh…" /></SelectTrigger>
            <SelectContent>
              {ENDPOINT_PRESETS.map((p) => (
                <SelectItem key={p.label} value={p.label}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-[120px_1fr] gap-2">
          <div>
            <Label>Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="playground-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["GET", "POST", "PATCH", "DELETE"].map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Path</Label>
            <Input value={path} onChange={(e) => setPath(e.target.value)}
              placeholder="/api/v1/health" className="font-mono text-xs" data-testid="playground-path-input" />
          </div>
        </div>
        {method !== "GET" && method !== "DELETE" && (
          <div>
            <Label>Body (JSON)</Label>
            <Textarea rows={10} value={body} onChange={(e) => setBody(e.target.value)}
              className="font-mono text-xs" data-testid="playground-body-input" />
          </div>
        )}
        <div className="flex justify-end">
          <Button onClick={onSend} disabled={busy} data-testid="playground-send-btn">
            <PaperPlaneTilt size={16} className="mr-1" />
            {busy ? "Mengirim…" : "Kirim Request"}
          </Button>
        </div>
      </div>

      {result && (
        <div className="border rounded-lg p-4 space-y-3" data-testid="playground-result">
          <div className="flex items-center gap-2">
            {result.ok ? <CheckCircle weight="fill" className="text-emerald-600" size={20} />
                       : <WarningCircle weight="fill" className="text-destructive" size={20} />}
            <span className="font-semibold">HTTP {result.status}</span>
            <span className="text-sm text-muted-foreground">{result.duration_ms} ms</span>
            {result.headers?.["x-ratelimit-remaining"] !== undefined && (
              <Badge variant="outline" className="ml-auto">
                Rate: {result.headers["x-ratelimit-remaining"]}/{result.headers["x-ratelimit-limit"]}
              </Badge>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label>Response Body</Label>
              <Button size="sm" variant="ghost" onClick={() => copy(typeof result.body === "string" ? result.body : JSON.stringify(result.body, null, 2), "Response disalin")}>
                <Copy size={14} className="mr-1" /> Copy
              </Button>
            </div>
            <pre className="text-xs bg-muted p-3 rounded max-h-72 overflow-auto">
{typeof result.body === "string" ? result.body : JSON.stringify(result.body, null, 2)}
            </pre>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label>curl equivalent</Label>
              <Button size="sm" variant="ghost" onClick={() => copy(result.curl, "curl disalin")}>
                <Copy size={14} className="mr-1" /> Copy
              </Button>
            </div>
            <pre className="text-xs bg-muted p-3 rounded overflow-auto">{result.curl}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/*                               PAGE                                     */
/* ────────────────────────────────────────────────────────────────────── */
export default function Integrations() {
  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4" data-testid="integrations-page">
      <div className="flex items-center gap-2">
        <Plug size={28} weight="duotone" className="text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Integrasi</h1>
          <p className="text-sm text-muted-foreground">
            Manajemen API Keys, Webhooks, dan Playground untuk ERP eksternal.
          </p>
        </div>
      </div>

      <Tabs defaultValue="keys" className="w-full">
        <TabsList className="grid w-full md:w-[480px] grid-cols-3">
          <TabsTrigger value="keys" data-testid="tab-api-keys">
            <Key size={16} className="mr-1" /> API Keys
          </TabsTrigger>
          <TabsTrigger value="webhooks" data-testid="tab-webhooks">
            <PaperPlaneTilt size={16} className="mr-1" /> Webhooks
          </TabsTrigger>
          <TabsTrigger value="playground" data-testid="tab-playground">
            <TerminalWindow size={16} className="mr-1" /> Playground
          </TabsTrigger>
        </TabsList>
        <TabsContent value="keys" className="mt-4"><ApiKeysTab /></TabsContent>
        <TabsContent value="webhooks" className="mt-4"><WebhooksTab /></TabsContent>
        <TabsContent value="playground" className="mt-4"><PlaygroundTab /></TabsContent>
      </Tabs>
    </div>
  );
}
